import { t } from "@lingui/macro";
import { Plus, PlusCircle } from "@phosphor-icons/react";
import {
  Award,
  Certification,
  CustomSection,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Skill,
} from "@reactive-resume/schema";
import { Button, ScrollArea, Separator } from "@reactive-resume/ui";
import { Fragment, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Icon } from "@/client/components/icon";
import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { useResumeStore } from "@/client/stores/resume";

import { BasicsSection } from "./sections/basics";
import { SectionBase } from "./sections/shared/section-base";
import { SectionIcon } from "./sections/shared/section-icon";
import { SummarySection } from "./sections/summary";
import { useBuilderStore } from "@/client/stores/builder";
import { Checkbox } from "@reactive-resume/ui";
import { useArtboardStore } from "@/artboard/store/artboard";

export const LeftSidebar = () => {
  const containterRef = useRef<HTMLDivElement | null>(null);

  const addSection = useResumeStore((state) => state.addSection);
  const customSections = useResumeStore((state) => state.resume.data.sections.custom);

  const scrollIntoView = (selector: string) => {
    const section = containterRef.current?.querySelector(selector);
    section?.scrollIntoView({ behavior: "smooth" });
  };
  const toggle = useBuilderStore((state) => state.toggle);
  const onToggle = (side: "left" | "right") => {
    // console.log(side)
    let abc: any = document.getElementsByClassName("bars-light")[0];
    abc.style.display = "block";
    abc = null;
    toggle(side);
  };

  const setValue = useResumeStore((state) => state.setValue);
  const basics = useResumeStore((state) => state.resume.data.basics);

  return (
    <div className="flex bg-secondary-accent/30">
      <div
        className="hidden basis-12 flex-col items-center justify-between bg-secondary-accent/30 py-4 sm:flex"
        style={{ display: "none" }}
      >
        <Button asChild size="icon" variant="ghost" className="size-8 rounded-full">
          <Link to="/dashboard">
            <Icon size={14} />
          </Link>
        </Button>

        <div className="flex flex-col items-center justify-center gap-y-2">
          <SectionIcon
            id="basics"
            onClick={() => scrollIntoView("#basics")}
            name={t({
              message: "Basics",
              context:
                "The basics section of a resume consists of User's Picture, Full Name, Location etc.",
            })}
          />
          <SectionIcon id="summary" onClick={() => scrollIntoView("#summary")} />
          <SectionIcon id="profiles" onClick={() => scrollIntoView("#profiles")} />
          <SectionIcon id="experience" onClick={() => scrollIntoView("#experience")} />
          <SectionIcon id="education" onClick={() => scrollIntoView("#education")} />
          <SectionIcon id="skills" onClick={() => scrollIntoView("#skills")} />
          <SectionIcon id="languages" onClick={() => scrollIntoView("#languages")} />
          <SectionIcon id="awards" onClick={() => scrollIntoView("#awards")} />
          <SectionIcon id="certifications" onClick={() => scrollIntoView("#certifications")} />
          <SectionIcon id="interests" onClick={() => scrollIntoView("#interests")} />
          <SectionIcon id="projects" onClick={() => scrollIntoView("#projects")} />
          <SectionIcon id="publications" onClick={() => scrollIntoView("#publications")} />
          <SectionIcon id="volunteer" onClick={() => scrollIntoView("#volunteer")} />
          <SectionIcon id="references" onClick={() => scrollIntoView("#references")} />

          <SectionIcon
            id="custom"
            variant="outline"
            name={t`Add a new section`}
            icon={<Plus size={14} />}
            onClick={() => {
              addSection();
              // eslint-disable-next-line lingui/no-unlocalized-strings
              scrollIntoView("& > section:last-of-type");
            }}
          />
        </div>

        <UserOptions>
          <Button size="icon" variant="ghost" className="rounded-full">
            <UserAvatar size={28} />
          </Button>
        </UserOptions>
      </div>

      <ScrollArea orientation="vertical" className="h-screen flex-1 pb-16 lg:pb-0">
        <button
          className="sb-icon"
          style={{ position: "absolute", top: 0, right: 0, zIndex: 99999 }}
          onClick={() => onToggle("left")}
        >
          Preview
        </button>
        <div ref={containterRef} className="grid gap-y-6 p-6 @container/left sidebar-desktop">
          <BasicsSection />
          <Separator />
          <SummarySection name="Work Summary" limit="300" />
          <Separator />
          <SectionBase<Profile>
            id="profiles"
            title={(item) => item.network}
            description={(item) => item.username}
            name="Social Profiles"
            tooltip={t`social profiles`}
          />
          <Separator />
          <SectionBase<Experience>
            id="experience"
            title={(item) => item.company}
            description={(item) => item.position}
            name="Experience"
            tooltip={t`experience`}
          />
          <Separator />
          <SectionBase<Education>
            id="education"
            title={(item) => item.institution}
            description={(item) => item.area}
            name="Education"
            tooltip={t`education`}
          />
          <Separator />
          <SectionBase<Skill>
            id="skills"
            title={(item) => item.name}
            description={(item) => {
              if (item.description) return item.description;
              if (item.keywords.length > 0) return `${item.keywords.length} keywords`;
            }}
            name="Skills"
            tooltip={t`skills`}
          />
          <Separator />
          <SectionBase<Language>
            id="languages"
            title={(item) => item.name}
            description={(item) => item.description}
            name="Languages"
            tooltip={t`languages`}
          />
          <Separator />
          <SectionBase<Award>
            id="awards"
            title={(item) => item.title}
            description={(item) => item.awarder}
            name="Awards"
            tooltip={t`awards`}
          />
          <Separator />
          <SectionBase<Certification>
            id="certifications"
            title={(item) => item.name}
            description={(item) => item.issuer}
            name="Certifications"
            tooltip={t`certifications`}
          />
          <Separator />
          <SectionBase<Interest>
            id="interests"
            title={(item) => item.name}
            description={(item) => {
              if (item.keywords.length > 0) return `${item.keywords.length} keywords`;
            }}
            name="Area of Interest"
            tooltip={t`area of interest`}
          />
          <Separator />

          <div className="space-y-1.5 sm:col-span-2 pb-10 sm:mb-6">
            <div className="flex items-center">
              {/* <Checkbox onChange={(event) => setValue("basics.logoFlag", "true")} />{" "} */}
              <Checkbox
                style={{
                  color: "#fff",
                  backgroundColor: basics.logoFlag ? "#0B6BBE" : "#fff",
                  border: basics.logoFlag ? "none" : "2px solid #0B6BBE",
                  height: "18px",
                  width: "18px",
                  marginRight: "10px",
                }}
                id="basics.logoFlag"
                checked={basics.logoFlag}
                onCheckedChange={(checked) => {
                  setValue("basics.logoFlag", checked);
                }}
              />
              <label className="font-medium text-base text-[#000000]">
                {" "}
                Show MY Bharat Logo on CV
              </label>
            </div>
          </div>
          {/* <div className="space-y-1.5 sm:col-span-2" onClick={logoShowHide}>
            <Checkbox /> <label> Show my bharat logo on CV</label>
          </div> */}
          {/* <Separator /> */}
          {/* <SectionBase<Publication>
            id="publications"
            title={(item) => item.name}
            description={(item) => item.publisher}
            name='Publications'
          /> */}
          {/* <Separator /> */}
          {/* <SectionBase<Volunteer>
            id="volunteer"
            title={(item) => item.organization}
            description={(item) => item.position}
            name='Volunteers'
          /> */}
          {/* <Separator /> */}
          {/* <SectionBase<Reference>
            id="references"
            title={(item) => item.name}
            description={(item) => item.description}
            name='Refrences'
          /> */}

          {/* Custom Sections */}
          {/* {Object.values(customSections).map((section) => (
            <Fragment key={section.id}>
              <Separator />

              <SectionBase<CustomSection>
                id={`custom.${section.id}`}
                title={(item) => item.name}
                description={(item) => item.description}
              />
            </Fragment>
          ))} */}

          {/* <Separator /> */}

          {/* <Button size="lg" variant="outline" onClick={addSection}>
            <PlusCircle />
            <span className="ml-2">{t`Add a new section`}</span>
          </Button> */}
        </div>
      </ScrollArea>
    </div>
  );
};
